import { CONSENT_COUNTRIES } from './ads-config.ts';
export type AdConsent = 'granted' | 'not_required' | 'denied' | 'gpc' | 'unknown';
export const AD_CONSENT_COOKIE = (host: string) => (host.includes('-preview.') ? 'bc_consent_preview' : 'bc_consent');
export const AD_CLICK_COOKIE = (host: string) => (host.includes('-preview.') ? 'bc_click_preview' : 'bc_click');
const COUNTRIES = new Set(
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(
    ' ',
  ),
);
export function resolveAdConsent(input: {
  cookie?: string | null;
  gpcHeader?: string | null;
  country?: string | null;
}): AdConsent {
  if (input.gpcHeader === '1') return 'gpc';
  const match = /^ads:(granted|denied)\|analytics:(granted|denied)$/.exec(input.cookie ?? '');
  if (match) return match[1] === 'granted' ? 'granted' : 'denied';
  if (input.cookie != null) return 'unknown';
  const country = (input.country ?? '').toUpperCase();
  return COUNTRIES.has(country) && !CONSENT_COUNTRIES.has(country) ? 'not_required' : 'unknown';
}
export const adsAllowed = (state: unknown): boolean => state === 'granted' || state === 'not_required';
export function readAdCookie(header: string | null | undefined, name: string): string | null {
  const values = (header ?? '')
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.startsWith(name + '='));
  if (values.length !== 1) return values.length ? '' : null;
  try {
    return decodeURIComponent(values[0]!.slice(name.length + 1));
  } catch {
    return '';
  }
}
export const adToken = (value: unknown, max = 512): string | null =>
  typeof value === 'string' && value.length <= max && /^[A-Za-z0-9._~-]+$/.test(value) ? value : null;
export function parseAdClick(raw: string | null, now = Date.now()): Record<string, string | null> {
  try {
    const value = JSON.parse(raw ?? '{}');
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      !Number.isFinite(value.ts) ||
      value.ts > now + 60_000 ||
      value.ts < now - 90 * 86400_000
    )
      return {};
    return {
      gclid: adToken(value.gclid),
      gbraid: adToken(value.gbraid),
      wbraid: adToken(value.wbraid),
      fbclid: adToken(value.fbclid),
      fbc: adToken(value.fbclid) ? 'fb.1.' + Math.floor(value.ts) + '.' + value.fbclid : null,
    };
  } catch {
    return {};
  }
}
export interface AdSignals {
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  fbp: string | null;
  fbc: string | null;
  clientUserAgent: string | null;
  clientIp: string | null;
  adConsent: AdConsent;
}
export function readAdSignals(req: Request, enabled = false, allowLocalTest = false): AdSignals {
  const url = new URL(req.url),
    host = url.hostname,
    cookies = req.headers.get('cookie');
  const adConsent = resolveAdConsent({
    cookie: readAdCookie(cookies, AD_CONSENT_COOKIE(host)),
    gpcHeader: req.headers.get('sec-gpc'),
    country: (req as Request & { cf?: { country?: string } }).cf?.country,
  });
  const none = {
    gclid: null,
    gbraid: null,
    wbraid: null,
    fbclid: null,
    fbp: null,
    fbc: null,
    clientUserAgent: null,
    clientIp: null,
  };
  const permittedHost =
    ['https://www.blastoclean.com', 'https://blastoclean.com', 'https://app.blastoclean.com'].includes(url.origin) ||
    (allowLocalTest && ['localhost', '127.0.0.1'].includes(host));
  if (!enabled || !permittedHost || host.includes('-preview.') || !adsAllowed(adConsent))
    return { ...none, adConsent: enabled && permittedHost ? adConsent : 'unknown' };
  const click = parseAdClick(readAdCookie(cookies, AD_CLICK_COOKIE(host)));
  return {
    ...none,
    gclid: click.gclid ?? null,
    gbraid: click.gbraid ?? null,
    wbraid: click.wbraid ?? null,
    fbclid: click.fbclid ?? null,
    fbp: adToken(readAdCookie(cookies, '_fbp'), 128),
    fbc: adToken(readAdCookie(cookies, '_fbc'), 600) ?? click.fbc ?? null,
    clientUserAgent: req.headers.get('user-agent')?.slice(0, 512) ?? null,
    clientIp: req.headers.get('cf-connecting-ip'),
    adConsent,
  };
}
